import React, { useEffect, lazy, Suspense } from 'react';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamation } from '@fortawesome/pro-regular-svg-icons';
import styled from 'styled-components';
import { pageContext } from 'ctm-util-functions';
import { mechaColours } from 'ctm-components/components/mecha_components/mecha_styles';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: #f7f2cc;
    border: 1px solid #deb90f;
    border-radius: 16px;
`;

const Row = styled.div`
    display: flex;
    flex-direction: row;
    align-self: baseline;
    padding: 1.6rem;
    gap: 1.6rem;
`;

const Column = styled.div`
    display: flex;
    flex-direction: column;
`;

const BucketContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 98%;
    margin-bottom: 1rem;
`;

const Title = styled.h4`
    font-size: 1.8rem;
    font-weight: 700;
    color: ${() => mechaColours.linkSecondary};
`;

const Text = styled.p`
    font-size: 1.4rem;
    font-weight: 500;
    color: ${() => mechaColours.linkSecondary};
    margin-bottom: 0rem;
`;

const IconContainer = styled.div`
    display: flex;
    background: ${() => mechaColours.linkSecondary};
    height: 4rem;
    width: ${(props) => (props.isXs ? '7rem' : '4rem')};
    align-items: center;
    justify-content: center;
    border-radius: 100%;

    svg {
        display: flex;
        height: 2rem;
        width: 2rem;
    }
`;

const _AssumedQuestions = (props) => {
    const {
        isRegularDriverUpdated,
        isYoungerDriverUpdated,
        assumedQuestions,
        setAssumedQuestions,
        hasAssumedDriverClaims,
        hasAssumedDriverLicenceType,
        hasAssumedYoungerDriverLicenceType,
        hasAssumedDriverComprehensiveInsured,
        hasAssumedDriverComprehensiveInsuredYears
    } = props;
    const isCarVertical = pageContext.getVertical() === 'car-insurance';
    const isXs = pageContext.getProps().pageState.breakpoint === 'xs';
    const assumedQuestionsArray = JSON.parse(JSON.stringify(assumedQuestions));
    // eslint-disable-next-line no-shadow
    const updateAssumedQuestionArray = (assumedQuestions, questionTypeList) => {
        return assumedQuestions?.filter((question) => !questionTypeList.includes(question?.questionId));
    };

    // Only rendering required answer summaries when there are assumed questions for regular or younger driver
    const hasDriverAssumedQuestions = hasAssumedDriverLicenceType || hasAssumedDriverClaims
    || hasAssumedDriverComprehensiveInsured || hasAssumedDriverComprehensiveInsuredYears;
    let SummaryRegularDriver;
    let SummaryYoungerDriver;
    /* Assumed Questions is a generic component however, SummaryRegularDriver and SummaryYoungerDriver is only available for car-insurance, so
    we will dynamically import the component based on the vertical. Try to import the SummaryRegularDriver and SummaryYoungerDriver components
    when we use react lazy, the result of the import is a promise we need to resolve this promise as a fallback, we
    return an empty fragment even though react docs does not specifically mention the need to resolve the promise,
    the DOM will still return invalid element type error */
    if (isCarVertical) {
        // Because the components are exported differently we need to use a promise chain to access it
        if (hasDriverAssumedQuestions) {
            SummaryRegularDriver = lazy(() => import('../../../ctm-vertical-car/components/summary_list/summary_sections/summary_regular_driver')
                .then((module) => ({ default: module.SummaryRegularDriver || (() => <></>) })));
        }
        if (hasAssumedYoungerDriverLicenceType) {
            SummaryYoungerDriver = lazy(() => import('../../../ctm-vertical-car/components/summary_list/summary_sections/summary_younger_driver')
                .then((module) => ({ default: module.SummaryYoungerDriver || (() => <></>) })));
        }
    }
    /* We need to check what details have now been updated every time isRegularDriverUpdated or isYoungerDriverUpdated
        changes based on which we can remove the assumed question from store which was updated and hence controlling
        what gets rendered
    */
    useEffect(() => {
        if (isCarVertical && assumedQuestionsArray?.length > 0) {
            const assumedQuestionsToFilter = [];
            if (isRegularDriverUpdated) {
                if (hasAssumedDriverClaims) {
                    assumedQuestionsToFilter.push('regularDriverClaims');
                }
                if (hasAssumedDriverLicenceType) {
                    assumedQuestionsToFilter.push('regularDriverLicenceType');
                }
                if (hasAssumedDriverComprehensiveInsured) {
                    assumedQuestionsToFilter.push('regularDriverComprehensiveInsured');
                }
                if (hasAssumedDriverComprehensiveInsuredYears) {
                    assumedQuestionsToFilter.push('regularDriverComprehensiveInsuredYears');
                }
            }
            if (hasAssumedYoungerDriverLicenceType && isYoungerDriverUpdated) {
                assumedQuestionsToFilter.push('youngerDriverLicenceType');
            }
            if (assumedQuestionsToFilter.length > 0) {
                const updatedAssumedQuestionsArray = updateAssumedQuestionArray(assumedQuestionsArray, assumedQuestionsToFilter);
                setAssumedQuestions(updatedAssumedQuestionsArray);
            }
        }
    }, [isRegularDriverUpdated, isYoungerDriverUpdated, isCarVertical, assumedQuestionsArray.length]);

    if (!(hasDriverAssumedQuestions || hasAssumedYoungerDriverLicenceType)) {
        return null;
    }
    return (
        <Container>
            <Row>
                <IconContainer isXs={isXs}>
                    <FontAwesomeIcon icon={faExclamation} border={false} color="#FFFFFF" size="3x" />
                </IconContainer>
                <Column>
                    <Title>Information required</Title>
                    <Text>We’ve made improvements to our quoting process to make it more accurate.</Text>
                    <Text>Please update these details to proceed.</Text>
                </Column>
            </Row>
            {isCarVertical && hasDriverAssumedQuestions && (
                <BucketContainer>
                    <Suspense>
                        <SummaryRegularDriver />
                    </Suspense>
                </BucketContainer>
            )}
            {isCarVertical && hasAssumedYoungerDriverLicenceType && (
                <BucketContainer>
                    <Suspense>
                        <SummaryYoungerDriver />
                    </Suspense>
                </BucketContainer>
            )}
        </Container>
    );
};

const mapDispatchToProps = (dispatch) => ({
    setAssumedQuestions: (questionList) => {
        dispatch({
            type: 'SET_VALUE',
            value: questionList,
            stateKey: 'vertical.quote',
            identifier: 'assumedQuestions'
        });
    }
});
const mapStateToProps = (state) => {
    return {
        assumedQuestions: state.getIn(['vertical', 'quote', 'form', 'assumedQuestions']) ?? [],
        isRegularDriverUpdated: state.getIn(['vertical', 'quote', 'form', 'summaryPageEditregular_driver_gender']),
        isYoungerDriverUpdated: state.getIn(['vertical', 'quote', 'form', 'summaryPageEdityoungest_driver']),
        hasAssumedDriverClaims: state.getIn(['vertical', 'quote', 'form', 'hasAssumedQuestions', 'hasAssumedDriverClaims']),
        hasAssumedDriverLicenceType: state.getIn(['vertical', 'quote', 'form', 'hasAssumedQuestions', 'hasAssumedDriverLicenceType']),
        hasAssumedYoungerDriverLicenceType: state.getIn(['vertical', 'quote', 'form', 'hasAssumedQuestions', 'hasAssumedYoungerDriverLicenceType']),
        hasAssumedDriverComprehensiveInsured: state.getIn(['vertical', 'quote', 'form', 'hasAssumedQuestions', 'hasAssumedDriverComprehensiveInsured']),
        hasAssumedDriverComprehensiveInsuredYears: state.getIn(['vertical', 'quote', 'form', 'hasAssumedQuestions', 'hasAssumedDriverComprehensiveInsuredYears'])
    };
};

const AssumedQuestions = connect(
    mapStateToProps,
    mapDispatchToProps
)(_AssumedQuestions);

export { _AssumedQuestions, AssumedQuestions };
